<template>
	<div class="pagination">
		<div class="pagination__details">
			<div class="pagination__display">
				<!--DEV NOTE: This element is optional-->
				Per page
				<div class="dropdown">
					<!--DEV NOTE: Code For Placement Only.  Will be replaced with Dropdown component-->

					<DropdownV2
						v-model="localValue.countPerPage"
						:options="ddPerPageOptions"
						:showClearSelectedButton="false"
						@change="updateCountPerPage"
					/>

					<div class="dropdown__icon-container">
						<span class="dropdown__icon">
							<i class="icon icon-select-down"></i>
						</span>
					</div>
				</div>
			</div>
			<div class="pagination__results">
				<div class="pagination__results-jump">
					<!--DEV NOTE: This element is optional-->
					Jump to
					<div class="dropdown">
						<DropdownV2
							v-model="localValue.page"
							:options="pages"
							:showClearSelectedButton="false"
							@change="updatePage"
						/>
					</div>
				</div>
				<div class="pagination__results-count">
					<span
						class="pagination__results-count--bold"
						data-test="resultCounter"
						>{{ resultCounter }}</span
					>
				</div>
			</div>
		</div>
		<div class="pagination__buttons">
			<Button
				className="btn--primary btn--has-icon"
				iconClassName="icon-arrow-left"
				:btnClick="onClickPreviousPage"
				:iconBeforeLabel="true"
				labelText="Previous"
				:disabled="localValue.page === 1"
			/>
			<Button
				className="btn--primary btn--has-icon"
				iconClassName="icon-arrow-right"
				:btnClick="onClickNextPage"
				:iconBeforeLabel="false"
				labelText="Next"
				:disabled="localValue.page === localValue.totalPageCount"
			/>
		</div>
	</div>
</template>

<!----------------- NOTES FOR DEV ------------------------------------
-- On mobile, when the user taps a dropdown, the select options should open in a scrollable select at the bottom of the screen
------------>

<script>
import Button from '@/components/forms/button/button';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import { generateArray } from '@/utils/function';

export default {
	name: 'Pagination',
	components: { Button, DropdownV2 },
	props: {
		value: {
			type: Object,
			required: true
		},
		isArrowUp: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			localValue: Object.assign({}, this.value)
		};
	},
	computed: {
		resultCounter() {
			const { page, countPerPage, totalCount } = this.value;
			let counts =
				page * countPerPage < totalCount
					? page * countPerPage
					: totalCount;
			return (
				(page - 1) * countPerPage +
				1 +
				' - ' +
				counts +
				' of ' +
				totalCount
			);
		},
		pages() {
			const newArray = generateArray(1, this.value.totalPageCount);
			return newArray.map(item => {
				return { value: item, text: item.toString() };
			});
		},
		ddPerPageOptions() {
			const range = [10, 20, 30, 50, 100];
			return range.map(value => ({ value, text: value.toString() }));
		}
	},
	methods: {
		onClickPreviousPage(e) {
			if (e) e.preventDefault();
			this.localValue.page = Math.max(1, this.localValue.page - 1);
			this.$emit('input', Object.assign({}, this.localValue));
		},
		onClickNextPage(e) {
			if (e) e.preventDefault();

			this.localValue.page = Math.min(
				this.localValue.totalPageCount,
				this.localValue.page + 1
			);
			this.$emit('input', Object.assign({}, this.localValue));
		},
		updateCountPerPage() {
			this.$emit(
				'input',
				Object.assign({}, { ...this.localValue, page: 1 })
			);
		},
		updatePage() {
			this.$emit('input', Object.assign({}, this.localValue));
		}
	},
	watch: {
		value(newValue) {
			this.localValue = Object.assign({}, newValue);
		}
	}
};
</script>

<style lang="scss">
/**
    * Pagination Styles
    * Can use SCSS variables = TRUE
 */

/*------------------------------------*\
  #BASE
\*------------------------------------*/
@import '@/scss/5-objects/_form.scss';

.pagination {
	display: flex;
	align-items: center;
	font-size: $default-font-size - 4;
	@include mq($until: md) {
		flex-wrap: wrap;
	}

	&__details {
		display: flex;
		justify-content: space-between;
		width: 100%;
	}

	&__display {
		display: flex;
		align-items: center;
		@include mq($until: md) {
			flex: 1 0 15%;
		}

		+ .pagination__results {
			@include mq($until: md) {
				justify-content: flex-end;
			}
		}
	}

	&__results {
		display: flex;
		align-items: center;
		margin-left: auto;
		@include mq($until: md) {
			flex: 1 0 33%;
			justify-content: center;
		}

		&-jump {
			display: flex;
			align-items: center;
			margin-right: $gutter-xl;
			@include mq($until: md) {
				margin-right: $gutter-md;
			}
		}

		&-count--bold {
			font-weight: $bold-font-weight;
		}
	}
}

/*------------------------------------*\
  #PAGINATION BUTTONS
\*------------------------------------*/

.pagination__buttons {
	display: flex;
	margin-left: $gutter-xl;

	@include mq($until: md) {
		flex-basis: 100%;
		margin: $gutter-xl 0 0 0;
		justify-content: space-between;
	}

	.btn {
		&:first-child {
			margin-right: $gutter-lg;

			@include mq($until: md) {
				margin-right: 0;
			}
		}

		@include mq($until: md) {
			width: 45%;
		}

		@include mq($from: md) {
			min-width: auto;
			border-radius: 50%;

			.icon {
				margin: 0;
				display: inline-flex;
				align-items: center;
				&:before {
					width: 1em;
				}
			}
		}

		&__label {
			@include mq($from: md) {
				display: none;
			}
		}
	}
}
</style>

<style lang="scss" scoped>
::v-deep .btn--has-icon .icon {
	&:first-child {
		margin-right: 0;
		transform: translateX(-1px); //visually centered
	}

	&:last-child {
		margin-left: 0;
		transform: translateX(1px); //visually centered
	}
}

::v-deep .dropdown {
	position: relative;
	margin-left: $gutter-md;

	&__icon-container {
		position: absolute;
		right: $input-padding-x;
		top: $input-padding-y;
	}

	&__icon {
		padding-left: $gutter-sm;
		font-size: $input-font-size + 2;

		&--arrow {
			margin: $gutter-sm;
			font-size: 8px;
		}
	}
}

::v-deep select {
	width: 100%;
	-webkit-user-select: none;
	-moz-user-select: -moz-none;
	-ms-user-select: none;
	user-select: none;
	-webkit-appearance: none;
	-moz-appearance: radio-container;
	appearance: none;

	option {
		&:checked {
			background-color: $neutral-4;
		}

		&:hover,
		&:active {
			background-color: lighten($neutral-4, 5%);
		}
	}
}
</style>
